import { type EventInput } from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGetManyReference, useGetOne, useRecordContext } from "react-admin";
import { useLocation } from "react-router";
import { atom, useRecoilState } from "recoil";
import {
  MissingAddressDataError,
  Recommendation,
  RecommendationsData,
  useStudentRecommendationsQuery,
} from "../../api/backoffice.api";
import { PostalAddress } from "../../model/PostalAddress";
import { AutovioCalendarEvent } from "../../model/autovioCalendarEvents";
import { Instructor } from "../../providers/instructorsProvider";
import { Student } from "../../providers/studentsProvider";
import { BookedTraining } from "../../model/BookedTraining";
import { PrivateAppointment } from "../../providers/instructorPrivateAppointmentsProvider";
import { RecommendationSettings } from "../../providers/instructorRecommendationSettingsProvider";
import { WorkTimeDayEnum, WorkTimes } from "../../providers/instructorWorkTimesProvider";
import { autovioColors, overviewCardStyle } from "../backofficeTheme";
import { AddAppointmentDialog } from "../../dialogs/AddAppointmentDialog/AddAppointmentDialog";
import { EventContent } from "./CalendarEventCardContent";
import { LoadingIndicator } from "../LoadingIndicator";
import { autovioCalendarEventToEventInput, calendarSlotLabelFormat, eventClassNames } from "../../utils/calendar";
import { useDialog } from "../../hooks/useDialog";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { useDateFromHashOrNow } from "../../hooks/useDateFromHashOrNow";
import { InstructorCalendarStats } from "./InstructorCalendarStats";
import { Column } from "../Column";
import { grants } from "../../backoffice.access_control";
import { useRefetchCalendarEventsCounter } from "../../hooks/useRefetchCalendarEventsCounter";
import { createPortal } from "react-dom";
import {
  InstructorCalendarHeaderMenuButton,
  isInstructorCalendarView,
  type InstructorCalendarView,
} from "./InstructorCalendarHeaderMenuButton";
import { Row } from "../Row";
import { InstructorCalendarRecommendationFilter } from "./InstructorCalendarRecommendationFilter";
import { DateRange } from "../../model/DateRange";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { serverAPI, WorktimeStatsDto2 } from "../../api/server.api";
import { persistentCalendarViewStateFamily } from "../../persistent_state";
import { useInstructorCalendarWarnings } from "../../hooks/useInstructorCalendarWarnings";
import { CalendarDayHeaderContent } from "./CalendarDayHeaderContent";
import { drivingSchoolsProvider } from "../../providers/drivingSchoolsProvider";
import { useFullcalendarCustomButtonAnchor } from "../../hooks/useFullcalendarCustomButtonAnchor";
import { type TimeRange } from "../../model/TimeRange";

export function useIsDebug() {
  const { search } = useLocation();
  return new URLSearchParams(search).get("debug") !== null;
}

interface CalendarData {
  data: {
    instructorEvents?: AutovioCalendarEvent[];
    privateAppointments?: PrivateAppointment[];
    recommendationsData?: RecommendationsData;
    workTimes?: WorkTimes;
    worktimeStats: WorktimeStatsDto2 | null;
    recommendationSettings?: RecommendationSettings;
  };
  isLoading: boolean;
  error: unknown;
}

/**
 *  If [withStudentConstraints] is true, uses the same constraints (max lessons per week, max days in advance)
 *   that apply when a student gets recommendations.
 */
function useCalendarData(props: {
  dateRange: DateRange;
  selectedStudent?: Student;
  selectedBookedTraining?: BookedTraining;
  selectedStartLocation?: PostalAddress;
  withStudentConstraints?: boolean;
}): CalendarData {
  const isDebug = useIsDebug();
  const instructor = useRecordContext<Instructor>();
  const [worktimeStats, setWorktimeStats] = useState<WorktimeStatsDto2 | null>(null);
  const { dateRange, selectedStudent, selectedBookedTraining } = props;
  const { id: instructorId, drivingSchoolId } = instructor;
  const refetchCounter = useRefetchCalendarEventsCounter();
  const {
    data: instructorEvents,
    isLoading: isLoading1,
    error: error1,
  } = useGetManyReference<AutovioCalendarEvent>("calendarEvents", {
    target: "drivingSchoolId",
    id: drivingSchoolId,
    filter: { dateRange, instructorId },
    pagination: { page: 1, perPage: 9999 },
    meta: { refetchCounter, withLessonsCanceledAtShortNotice: true },
  });
  const {
    data: privateAppointments,
    isLoading: isLoading2,
    error: error2,
  } = useGetManyReference<PrivateAppointment>("instructorPrivateAppointments", {
    target: "instructorId",
    id: instructorId,
    filter: { dateRange },
    pagination: { page: 1, perPage: 9999 },
  });
  const {
    data: workTimes,
    isLoading: isLoading3,
    error: error3,
  } = useGetOne<WorkTimes>("instructorWorkTimes", {
    id: instructorId.toString(),
  });
  const bookedTrainings = selectedStudent?.bookedTrainings;
  const {
    data: recommendationsData,
    isLoading: isLoading4,
    error: error4,
  } = useStudentRecommendationsQuery(
    selectedStudent && bookedTrainings && bookedTrainings.length > 0
      ? {
          dateRange,
          bookedTrainingId: (selectedBookedTraining ?? selectedStudent.activeOrMostRecentBookedTraining).id,
          student: selectedStudent,
          instructorId: instructorId.toString(),
          startLocation: props.selectedStartLocation,
          options: {
            returnRemoved: isDebug,
            ...(props.withStudentConstraints
              ? {}
              : {
                  ignoreMaxDaysInAdvance: true,
                  ignoreMaxBookableDrivingLessonsPerWeek: true,
                }),
          },
        }
      : undefined,
  );
  const {
    data: recommendationSettings,
    isLoading: isLoading5,
    error: error5,
  } = useGetOne<RecommendationSettings>("instructorRecommendationSettings", {
    id: instructorId.toString(),
  });

  useEffect(() => {
    const fetchInstructorWorktimeStats = async () => {
      setWorktimeStats(null); // ... hide outdated stats when dateRange changed
      try {
        const instructorWorktimeStats = await serverAPI.getInstructorWorktimeStats(instructorId, dateRange);
        setWorktimeStats(instructorWorktimeStats);
      } catch (error) {
        console.error("Error fetching instructor worktime stats:", error);
      }
    };

    void fetchInstructorWorktimeStats();
  }, [dateRange[0].toString(), dateRange[1].toString(), instructorId]);

  return {
    data: {
      instructorEvents: instructorEvents?.filter((it) => it.type !== "TheoryExam"),
      recommendationsData,
      privateAppointments,
      workTimes,
      recommendationSettings,
      worktimeStats,
    },
    isLoading: isLoading1 || isLoading2 || isLoading3 || isLoading4 || isLoading5,
    error: error1 || error2 || error3 || error4 || error5,
  };
}

export const selectedStartLocationState = atom<PostalAddress | undefined>({
  key: "InstructorEventsCalendar.selectedStartLocation",
  default: undefined,
});

export function InstructorCalendar() {
  const calendarRef = useRef<FullCalendar>(null);
  const isDebug = useIsDebug();
  const date = useDateFromHashOrNow();
  const [calendarTitle, setCalendarTitle] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>([
    DateWithoutTime.fromDateTime(date.startOf("week")),
    DateWithoutTime.fromDateTime(date.endOf("week")),
  ]);
  const [selectedStudent, setSelectedStudent_] = useState<Student | undefined>();
  const [selectedBookedTraining, setSelectedBookedTraining] = useState<BookedTraining | undefined>();
  const [showRecommendationFilters, setShowRecommendationFilters] = useState<boolean>(false);
  const threeDotsMenuAnchor = useFullcalendarCustomButtonAnchor("threeDotsMenuAnchor");
  const setSelectedStudent = (student: Student | undefined) => {
    setSelectedStudent_(student);
    setSelectedBookedTraining(undefined);
  };
  const [selectedStartLocation, setSelectedStartLocation] = useRecoilState(selectedStartLocationState);
  const [withStudentConstraints, setWithStudentConstraints] = useState(false);
  const { data, isLoading, error } = useCalendarData({
    dateRange,
    selectedStudent,
    selectedBookedTraining,
    selectedStartLocation,
    withStudentConstraints,
  });
  const instructor = useRecordContext<Instructor>();
  const missingAddressInfo = error instanceof MissingAddressDataError;
  const minHour = data?.instructorEvents?.reduce((acc, it) => Math.min(acc, it.start.hour), 6) ?? 6;
  const { dialogProps, openDialog } = useDialog();
  const [{ drivingSchoolId }] = useAutovioContext();
  const [persistedView, setPersistedView] = useRecoilState(persistentCalendarViewStateFamily("InstructorCalendar"));
  const calendarView = isInstructorCalendarView(persistedView) ? persistedView : "timeGridWeek";
  const changeCalendarView = useCallback((view: InstructorCalendarView) => {
    calendarRef.current?.getApi().changeView(view);
  }, []);
  const canCreateDrivingLessonOrExam = grants.has("DrivingLesson:create") || grants.has("Exam:create");
  const { warningsByDay, warningsByEventId } = useInstructorCalendarWarnings(data.instructorEvents, data.worktimeStats);

  if (!drivingSchoolId) {
    return null;
  }

  return (
    <Column className="instructor-calendar" data-testid="InstructorCalendar">
      <Row
        data-testid="instructor-calendar-stats-card"
        sx={{
          ...overviewCardStyle,
          gap: { xs: 2, sm: "36px" },
          alignItems: "center",
          flexWrap: "wrap",
          p: { xs: "8px 16px", sm: "15px 30px" },
        }}
      >
        {showRecommendationFilters ? (
          <InstructorCalendarRecommendationFilter
            instructor={instructor}
            selectedStudent={selectedStudent}
            selectedBookedTraining={selectedBookedTraining}
            setSelectedStudent={setSelectedStudent}
            setSelectedBookedTraining={setSelectedBookedTraining}
            setSelectedStartLocation={setSelectedStartLocation}
            setShowRecommendationFilters={setShowRecommendationFilters}
          />
        ) : (
          <InstructorCalendarStats
            worktimeStats={data.worktimeStats}
            dateRange={dateRange}
            calendarView={calendarView}
          />
        )}
      </Row>
      {isDebug && selectedStudent ? (
        <FormControlLabel
          label="Debug: Terminvorschläge aus Sicht des Schülers anzeigen"
          checked={withStudentConstraints}
          control={<Checkbox onChange={(event) => setWithStudentConstraints(event.target.checked)} />}
        />
      ) : (
        <Box sx={{ height: "20px" }} />
      )}
      {missingAddressInfo && (
        <Typography sx={{ mb: "20px", color: autovioColors.red }}>Dem Schüler fehlen Addressinformationen</Typography>
      )}
      <Box sx={{ ...overviewCardStyle, p: 3, mt: 0 }}>
        <div className="instructor-calendar-card-header">
          <h2 className="fc-toolbar-title instructor-calendar-title">{calendarTitle}</h2>
          <InstructorCalendarHeaderMenuButton
            activeCalendarView={calendarView}
            changeCalendarView={changeCalendarView}
            setShowRecommednationFilters={setShowRecommendationFilters}
          />
        </div>
        <FullCalendar
          ref={calendarRef}
          timeZone="Europe/Berlin"
          locales={[deLocale]}
          height="auto"
          slotMinTime={`0${minHour}:00:00`}
          slotLabelFormat={calendarSlotLabelFormat}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          eventMinHeight={1}
          headerToolbar={{
            left: "title threeDotsMenuAnchor",
            center: "",
            right: `prev today next ${
              !selectedStudent
                ? `${canCreateDrivingLessonOrExam ? "addAppointmentButton " : ""}timeGridDay,timeGridWeek,dayGridMonth`
                : "timeGridDay,timeGridWeek,dayGridMonth"
            }`,
          }}
          customButtons={{
            addAppointmentButton: { text: "＋\u00A0Termin", click: openDialog },
            threeDotsMenuAnchor: {},
          }}
          initialDate={date.toISO()}
          initialView={calendarView}
          scrollTime="08:00:00"
          allDaySlot={false}
          editable={false}
          weekNumbers={true}
          showNonCurrentDates={false}
          dayMaxEvents={true}
          nowIndicator={true}
          events={[
            ...(data.recommendationsData?.recommendations?.map(_recommendationToEventInput) ?? []),
            ...(isDebug
              ? (data.recommendationsData?.removedRecommendations?.map(_removedRecommendationToEventInput) ?? [])
              : []),
            ...(data.instructorEvents?.map((event) =>
              autovioCalendarEventToEventInput(event, { warnings: warningsByEventId.get(event.id) }),
            ) ?? []),
            ...(data.instructorEvents?.flatMap(_bufferTimesToEventInput) ?? []),
            ...(data.privateAppointments?.map(_privateAppointmentToEventInput) ?? []),
            ...(data.workTimes && data.recommendationSettings
              ? _workTimesToEventInputs({
                  workTimes: data.workTimes,
                  recommendationSettings: data.recommendationSettings,
                  dateRange,
                })
              : []),
          ]}
          dayHeaderContent={(arg) => <CalendarDayHeaderContent arg={arg} warningsByDay={warningsByDay} />}
          eventClassNames={eventClassNames}
          eventContent={(eventInfo) => <EventContent eventInfo={eventInfo} mode="InstructorCalendar" />}
          datesSet={(dates) => {
            setCalendarTitle(dates.view.title);
            if (dates.view?.type && persistedView !== dates.view.type) {
              setPersistedView(dates.view.type);
            }
            const start = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.start, { zone: "Europe/Berlin" }));
            const end = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.end, { zone: "Europe/Berlin" })).prevDay;
            if (dateRange[0].toString() !== start.toString() || dateRange[1].toString() !== end.toString()) {
              setDateRange([start, end]);
            }
          }}
          dateClick={
            canCreateDrivingLessonOrExam ? AddAppointmentDialog.fullCalendarDateClickHandler(openDialog) : undefined
          }
        />
      </Box>
      {threeDotsMenuAnchor &&
        (createPortal(
          <InstructorCalendarHeaderMenuButton setShowRecommednationFilters={setShowRecommendationFilters} />,
          threeDotsMenuAnchor,
        ) as any)}
      <AddAppointmentDialog {...dialogProps} instructor={instructor} tabs={["drivingLesson", "exam", "otherEvent"]} />
      {isLoading && <LoadingIndicator />}
    </Column>
  );
}

const _privateAppointmentToEventInput = (privateAppointment: PrivateAppointment): EventInput => ({
  title: "Privater Termin",
  id: privateAppointment.id,
  backgroundColor: autovioColors.greyUltraLight,
  color: autovioColors.greyUltraLight,
  textColor: autovioColors.black,
  editable: false,
  start: privateAppointment.start.toISO(),
  end: privateAppointment.end.toISO(),
});

const _workTimesToEventInputs = ({
  workTimes,
  recommendationSettings,
  dateRange,
}: {
  workTimes: WorkTimes;
  recommendationSettings: RecommendationSettings;
  dateRange: DateRange;
}): EventInput[] => {
  const startDate = dateRange[0].toDateTime();
  const endDate = DateTime.min(
    dateRange[1].toDateTime().endOf("day"),
    DateTime.now().plus({ days: recommendationSettings.maxDaysBookableInAdvance }).endOf("day"),
  );

  if (startDate > endDate) {
    return [];
  }

  const workTimesPerDate: { [k: string]: any } = {};
  for (let date = startDate; date.endOf("day") < endDate; date = date.plus({ days: 1 })) {
    const weekDay = WorkTimeDayEnum.options[date.weekday - 1];
    workTimesPerDate[date.toISO()] = workTimes[weekDay];
  }

  const eventInputs: EventInput[] = Object.entries(workTimesPerDate).flatMap(([date, timeRanges]) =>
    timeRanges.map((it: TimeRange) => {
      return {
        display: "background",
        start: DateTime.fromISO(date).plus({ hours: it.start.hour, minutes: it.start.minute }).toISO(),
        end: DateTime.fromISO(date).plus({ hours: it.end.hour, minutes: it.end.minute }).toISO(),
      };
    }),
  );
  return eventInputs;
};

const _recommendationToEventInput = (recommendation: Recommendation): EventInput => ({
  title: "Terminvorschlag",
  id: recommendation.id,
  backgroundColor: "#FAE5E5CC",
  color: autovioColors.black,
  textColor: autovioColors.black,
  editable: false,
  start: recommendation.start.toISO(),
  end: recommendation.end.toISO(),
  extendedProps: {
    bookable: true,
    recommendation: recommendation,
  },
});

const _removedRecommendationToEventInput = (recommendation: Recommendation): EventInput => ({
  title: "ENTFERNT",
  id: recommendation.id,
  backgroundColor: autovioColors.red,
  color: autovioColors.black,
  textColor: autovioColors.black,
  editable: false,
  start: recommendation.start.toISO(),
  end: recommendation.end.toISO(),
  extendedProps: {
    recommendation: recommendation,
  },
});

const _bufferTimesToEventInput = (event: AutovioCalendarEvent): EventInput[] => {
  const inputs: EventInput[] = [];
  const preparationTime = event.bufferTimes?.preparationTime;
  const wrapUpTime = event.bufferTimes?.wrapUpTime;
  if (preparationTime) {
    inputs.push({
      title: _bufferTimeTitle(event, "preparationTime"),
      id: `${event.id}-prep`,
      backgroundColor: autovioColors.greyUltraLight,
      color: autovioColors.black,
      textColor: autovioColors.black,
      editable: false,
      start: event.start.minus({ minutes: preparationTime }).toISO(),
      end: event.start.toISO(),
    });
  }
  if (wrapUpTime) {
    inputs.push({
      title: _bufferTimeTitle(event, "wrapUpTime"),
      id: `${event.id}-wrapUp`,
      backgroundColor: autovioColors.greyUltraLight,
      color: autovioColors.black,
      textColor: autovioColors.black,
      editable: false,
      start: event.end.toISO(),
      end: event.end.plus({ minutes: wrapUpTime }).toISO(),
    });
  }
  return inputs;
};

const _bufferTimeTitle = (event: AutovioCalendarEvent, bufferTimeType: "preparationTime" | "wrapUpTime"): string => {
  switch (event.type) {
    case "DrivingLesson":
      return bufferTimeType === "preparationTime" ? "Anfahrt" : "Abfahrt";
    case "Other": {
      const drivingSchool = drivingSchoolsProvider.getOneFromCache(event.drivingSchoolId);
      const otherEventCategory = drivingSchool?.otherCalendarEventCategories[event.categoryId];
      if (
        otherEventCategory &&
        ["publicHoliday", "sickness", "vacation", "training"].includes(otherEventCategory.type)
      ) {
        return "Blocker";
      }
      break;
    }
    case "TheoryLesson":
      return bufferTimeType === "preparationTime" ? "Vorbereitung" : "Nachbereitung";
  }
  return "Pufferzeit";
};
